<script setup lang="ts">
import { computed, onMounted, watch } from "vue"
import { storeToRefs } from "pinia"
import { RouterView, useRoute } from "vue-router"

import { useI18nStore } from "@/stores/i18n"
import { useConfigStore } from "@/stores/config"
import { useUserStore } from "@/stores/user"
import { useExitIframeModalStore } from "@/components/af/ExitIframeModal/ExitIframeModal.store"
import { useBranding } from "@/composables/useBranding"

import ShortCut from "@/components/dev/ShortCut.vue"
import PreviousStateModal from "@/components/af/PreviousStateModal/PreviousStateModal.vue"
import LeaveAppModal from "@/components/af/LeaveAppModal/LeaveAppModal.vue"
import ExitIframeModal from "@/components/af/ExitIframeModal/ExitIframeModal.vue"
import LeaveWindowModal from "@/components/af/LeaveWindowModal/LeaveWindowModal.vue"
import { ALBackgroundGradient } from "@af-lib/vue-ui"

import HeaderControls from "@/components/af/HeaderControls/HeaderControls.vue"
import AfLogo from "@/assets/logo.png"

const userStore = useUserStore()
const { isResellerAcc } = storeToRefs(userStore)
const { isBlocked: isIframeBlocked } = storeToRefs(useExitIframeModalStore())
const configStore = useConfigStore()

const { currentLocale } = storeToRefs(useI18nStore())
const { appReady } = storeToRefs(useConfigStore())
const { applyBrandingStyle, applyBrandingFavicon, applyUnbrandedTitle } =
  useBranding()

// Watch user and update the favicon once the reseller user is available.
watch(
  isResellerAcc,
  (isReseller) => {
    if (isReseller) {
      applyBrandingFavicon()
      applyUnbrandedTitle()
    }
  },
  { immediate: true }
)

onMounted(async () => {
  applyBrandingStyle()
})

// const devMode = process.env.NODE_ENV === "development"

const route = useRoute()
const path = computed(() => route.path)

const isDevModeSkipAssessment = computed(() => {
  const isDevEnv =
    configStore.environment !== "production" ||
    configStore.urls.app.includes("simu.assessfirst.com")
  const isAssessmentSkipable = ["swipe", "drive", "brain", "move"].some(
    (assessment) => path.value.includes(assessment)
  )
  return isDevEnv && isAssessmentSkipable
})
</script>

<template>
  <!-- background -->
  <ALBackgroundGradient />

  <!-- Swipe: Quick actions -->
  <ShortCut v-if="isDevModeSkipAssessment" />

  <!-- app content is reactive to current locale -->
  <main
    :class="[
      'text-afbranding-text relative flex min-h-dvh w-full flex-col items-center justify-center py-16 lg:justify-center',
      currentLocale.iso_code_639 === 'ar-SA' && 'arabic',
    ]"
    :key="currentLocale.iso_code_639"
  >
    <!-- header bar with lang, help, etc -->
    <Transition
      enter-active-class="duration-600 ease-out"
      enter-from-class="-translate-y-full opacity-0"
      enter-to-class="translate-y-0 opacity-100"
    >
      <header
        v-if="appReady"
        class="border-af-grey-300 bg-af-white/40 fixed top-0 z-100 mt-0 w-full border-b px-5 py-2 backdrop-blur-xs"
      >
        <div class="flex items-center justify-between gap-4">
          <!-- box to teleports stuffs -->
          <img v-if="!isResellerAcc" :src="AfLogo" alt="" class="h-12 w-12" />
          <div id="box-heading" class="ms-auto"></div>
          <div class="flex items-center gap-4">
            <!-- Top right controls -->
            <HeaderControls />
          </div>
        </div>
      </header>
    </Transition>

    <!-- main view -->
    <RouterView v-slot="{ Component, route: currentRoute }">
      <Transition
        mode="out-in"
        enter-active-class="duration-300 ease-out"
        enter-from-class="transform opacity-0 -translate-x-3"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="duration-300 ease-in"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="transform opacity-0 translate-x-3"
      >
        <!--
          `mode="out-in"` only mounts the incoming view once the leaving one has
          finished its transition, and that callback only ever fires on a real
          element. A view that renders a fragment, a comment (root `v-if` that is
          false) or nothing at all therefore leaves the transition waiting forever:
          the URL moves on, the screen stays blank and the app never recovers.
          This wrapper is that element. It is keyed on the top-level route so the
          transition still plays exactly when the routed component changes, and it
          shields the transition from whatever the views render inside it.
        -->
        <div
          :key="currentRoute.matched[0]?.name ?? currentRoute.name"
          class="flex w-full flex-col items-center justify-center"
        >
          <Component :is="Component" />
        </div>
      </Transition>
    </RouterView>
    <!-- modal to handle and confirm previous action -->
    <PreviousStateModal />
    <!-- modal to leaving the App-->
    <LeaveAppModal />
    <!-- modal to trigger leaving window -->
    <LeaveWindowModal v-if="path.includes('voice/test')" />
    <!-- modal to leave app in new tab because of iframe -->
    <ExitIframeModal v-if="isIframeBlocked" />
  </main>
</template>
